import type { Vector2d } from '../../types'
import { getClosestPointOnRectangle } from '../../helper'
import { BaseEntityProperties } from './baseEntityProperties'
import { CANVAS_HANDLE_MODE, SHAPES, CANVAS_ENTITY_EVENT } from '../../const'
import type { IGroup, IPointerEvent, IShape } from '../../backend/IRenderer'
import { isEllipseNode, isLineNode, isRectNode } from '../../backend/nodeGuards'
import type { BalloonBoundingRect, TripodShapePoints } from '@/app/data/type'
import { Balloon, type BalloonConfig, type BalloonTagData } from './balloon'
import { SnapManager } from './balloon/snapManager'
import { RippleManager } from './balloon/rippleManager'

export { SnapDirection, SnapType } from './balloon/snapManager'

const BALLOON_LENGTH = -40

export class BaseBalloonExtension extends BaseEntityProperties {
  /**
   * 점을 주축선에 투영하여 주축선 상의 가장 가까운 점 찾기 (TRIPOD용)
   */
  private __projectPointOntoTripodMainAxis(
    point: { x: number; y: number },
    tripodShapePoints: TripodShapePoints,
  ): { x: number; y: number; t: number } {
    const mainAxisStart = {
      x: tripodShapePoints.mainAxisPoint1[0],
      y: tripodShapePoints.mainAxisPoint1[1],
    }
    const mainAxisEnd = {
      x: tripodShapePoints.mainAxisPoint2[0],
      y: tripodShapePoints.mainAxisPoint2[1],
    }

    // 주축선 벡터
    const mainAxisVector = {
      x: mainAxisEnd.x - mainAxisStart.x,
      y: mainAxisEnd.y - mainAxisStart.y,
    }

    // 점에서 주축선 시작점까지의 벡터
    const pointToAxisStart = {
      x: point.x - mainAxisStart.x,
      y: point.y - mainAxisStart.y,
    }

    // 주축선 벡터의 길이 제곱
    const mainAxisLengthSquared =
      mainAxisVector.x * mainAxisVector.x + mainAxisVector.y * mainAxisVector.y

    if (mainAxisLengthSquared === 0) {
      // 주축선 길이가 0인 경우 시작점 반환
      return { ...mainAxisStart, t: 0 }
    }

    // 투영 계수 계산 (0~1 범위로 클램핑하여 주축선 범위 내로 제한)
    const t = Math.max(
      0,
      Math.min(
        1,
        (pointToAxisStart.x * mainAxisVector.x + pointToAxisStart.y * mainAxisVector.y) /
          mainAxisLengthSquared,
      ),
    )

    // 투영점 계산
    return {
      x: mainAxisStart.x + mainAxisVector.x * t,
      y: mainAxisStart.y + mainAxisVector.y * t,
      t,
    }
  }

  // 모듈화된 Balloon 객체
  balloon?: Balloon

  protected balloonDragDiff?: {
    x: number
    y: number
  }

  // 스냅 매니저 (lazy initialization)
  private _snapManager?: SnapManager
  protected get snapManager(): SnapManager {
    if (!this._snapManager) {
      this._snapManager = new SnapManager(this)
    }
    return this._snapManager
  }

  // 리플 매니저 (lazy initialization)
  private _rippleManager?: RippleManager
  protected get rippleManager(): RippleManager {
    if (!this._rippleManager) {
      this._rippleManager = new RippleManager(this)
    }
    return this._rippleManager
  }

  // 드래그 상태 관리
  public isBalloonDragMode: boolean = false // balloon이 드래그 중인지 여부 (shape 생성과 구분)

  protected __otherBalloonBoundingRects: BalloonBoundingRect[] = []

  isBalloonMoved = false
  protected __admin_balloon_text: string = ''

  protected get balloonSize(): number {
    return this.calculatedBalloonSize
  }

  // Public getter for SnapManager
  public getBalloonSize(): number {
    return this.balloonSize
  }

  // ---- 경계 API: 바깥이 balloon.head.text/rect 등 내부 노드에 접근하지 않게 하는 값 getter ----

  /** 풍선 head 위치(엔티티 기준 상대좌표 = export의 balloonPoint). 풍선 없으면 undefined */
  public getBalloonPosition(): Vector2d | undefined {
    return this.balloon?.getPosition()
  }

  /** 풍선 head 드래그 중 여부 */
  public isBalloonDragging(): boolean {
    return this.balloon?.isDragging() ?? false
  }

  /** 풍선에 표시 중인 텍스트(번호). 풍선 없으면 '' */
  public getBalloonText(): string {
    return this.balloon?.head.text.text() ?? ''
  }

  /** 풍선 head 박스 크기·테두리 굵기(설정값 기준, scale 미적용). 풍선 없으면 undefined */
  public getBalloonHeadBox(): { width: number; height: number; strokeWidth: number } | undefined {
    if (!this.balloon) return undefined
    const rect = this.balloon.head.rect
    return { width: rect.width(), height: rect.height(), strokeWidth: rect.strokeWidth() }
  }

  protected get balloonThickness(): number {
    return this.calculatedBalloonThickness
  }
  protected get balloonFontSize(): number {
    return this.calculatedBalloonFontSize
  }
  protected get connectedLineWidth(): number {
    return this.calculatedConnectedLineWidth
  }
  protected get pointSize(): number {
    return this.calculatedPointSize
  }
  protected get areaLineWidth(): number {
    return this.calculatedAreaLineWidth
  }

  /**
   * DIRECTION shape의 현재 bend point 개수를 반환합니다.
   * @returns bend point 개수 (최대 2개)
   */
  public getCurrentBendPointCount(): number {
    if (this.entityType !== SHAPES.DIRECTION || !this.__directionPoints) {
      return 0
    }

    // direction points는 [startX, startY, bendPoint1X, bendPoint1Y, ...] 형태
    // 4개 이상이면 bend point가 있고, 점의 개수는 (points.length / 2) - 1
    const totalPoints = this.__directionPoints.length / 2
    return Math.max(0, totalPoints - 2) // start point와 end point를 제외한 bend point 개수
  }

  /**
   * 현재 balloon의 전역 위치를 계산하여 반환
   * @returns Vector2d - balloon의 전역 좌표
   */
  public getCurrentBalloonGlobalPosition(): Vector2d {
    if (!this.balloon) {
      return { x: 0, y: 0 }
    }

    // 현재 엔티티 중심점 계산
    let entityCenter: Vector2d = { x: 0, y: 0 }

    if (this.isLine()) {
      // 선의 경우 중앙점 계산
      const points = this.getPoints()
      entityCenter = {
        x: (points[0] + points[2]) / 2,
        y: (points[1] + points[3]) / 2,
      }
    } else if (
      this.entityType === SHAPES.DIRECTION &&
      this.__directionPoints &&
      this.__directionPoints.length >= 4
    ) {
      // DIRECTION shape의 경우 balloon이 끝점에 고정되어 있으므로
      // balloon의 상대 좌표가 이미 절대 좌표임
      const balloonPos = this.balloon.getPosition()
      return {
        x: balloonPos.x,
        y: balloonPos.y,
      }
    } else {
      // 일반 도형의 중심 좌표
      entityCenter = {
        x: this.node.x(),
        y: this.node.y(),
      }
    }

    // 현재 드래그 중인 벌룬의 전역 좌표 계산
    const balloonPos = this.balloon.getPosition()

    return {
      x: entityCenter.x + balloonPos.x,
      y: entityCenter.y + balloonPos.y,
    }
  }

  protected __isTransformInProgress = false

  onIndexChanged(index: string): void {
    super.onIndexChanged(index)
    if (!index) {
      this.setBalloonText(this.__admin_balloon_text)
    } else {
      this.setBalloonText(index)
    }
  }

  setBalloonCircle(value: boolean): void {
    if (!this.balloon) return
    this.balloon.setCircle(value)
  }

  setBalloonTag(tag: BalloonTagData): void {
    if (!this.balloon) return
    this.balloon.setTag(tag)
  }

  hideBalloonTag(): void {
    if (!this.balloon) return
    this.balloon.hideTag()
  }

  changeColor(color: string): void {
    super.changeColor(color)
    if (!this.balloon) return

    this.balloon.changeColor(color)

    // Fill 패턴이 적용된 경우 패턴 색상도 업데이트
    this.updateFillPatternColor(color)
  }

  changeBalloonFillColor(color: string): void {
    if (!this.balloon) return
    const opacity = this.canvasEngine.balloonFillOpacity
    this.balloon.changeFillColor(color, opacity)
    this.setBalloonFillColor(color)
  }

  changeBalloonFontColor(color: string): void {
    if (!this.balloon) return
    this.balloon.changeFontColor(color)
    this.setBalloonFontColor(color)
  }

  /**
   * Fill 패턴의 색상을 업데이트합니다
   * @param color 새로운 색상
   */
  private updateFillPatternColor(color: string): void {
    // RECT나 ELLIPSE 도형이고 개별 fill 설정이 true인 경우에만 실행
    if (!this.isRect() && !this.isEllipse()) return

    const individualFillSetting = this.getIndividualFillSetting()
    if (individualFillSetting !== true) return

    // 도형에 fill 패턴이 적용되어 있는 경우 새로운 색상으로 패턴 재생성
    const shape = this.avaliableShape
    if (shape && (isRectNode(shape) || isEllipseNode(shape))) {
      const shapeWidth = this.getShapeWidth()
      const shapeHeight = this.getShapeHeight()
      const newPatternImage = this.createDiagonalPattern(color, shapeWidth, shapeHeight)
      shape.fillPattern({ image: newPatternImage })
    }
  }

  /**
   * Transform 시 Fill 패턴을 업데이트합니다 (크기 변경에 따른 패턴 재생성)
   */
  private updateFillPatternOnTransform(): void {
    // RECT나 ELLIPSE 도형이고 개별 fill 설정이 true인 경우에만 실행
    if (!this.isRect() && !this.isEllipse()) return

    const individualFillSetting = this.getIndividualFillSetting()
    if (individualFillSetting !== true) return

    // 도형에 fill 패턴이 적용되어 있는 경우 현재 크기에 맞게 패턴 재생성
    const shape = this.avaliableShape
    if (shape && (isRectNode(shape) || isEllipseNode(shape))) {
      const shapeWidth = this.getShapeWidth()
      const shapeHeight = this.getShapeHeight()
      const newPatternImage = this.createDiagonalPattern(this.__color, shapeWidth, shapeHeight)
      shape.fillPattern({ image: newPatternImage })
    }
  }

  select(): void {
    super.select()
    if (!this.balloon) return
    this.balloon.moveToTop()
    this.balloon.changeColor(this.__color)
    this.rippleManager.addShapeAnimation()
    this.updateBalloonPosition()
  }

  blur(): void {
    super.blur()
    if (!this.balloon) return

    this.updateBalloonPosition()

    // 파동 애니메이션 정리
    this.rippleManager.removeShapeAnimation()
  }

  /**
   * 선에 연결하는 위치를 선의 어디 부분으로 정할지 정하는 함수
   * @param points 선의 좌표
   * @returns 선에 연결하려고 하는 좌표
   */
  protected connectToLinePosition(points?: number[]): Vector2d {
    if (points) {
      return {
        x: (points[0] + points[2]) / 2,
        y: (points[1] + points[3]) / 2,
      }
    }
    return {
      x: (this.getPoints()[0] + this.getPoints()[2]) / 2,
      y: (this.getPoints()[1] + this.getPoints()[3]) / 2,
    }
  }

  getBalloonAbsolutePosition(pos: Vector2d): Vector2d {
    const entityX = this.node.x()
    const entityY = this.node.y()
    return {
      x: entityX + pos.x,
      y: entityY + pos.y,
    }
  }

  /**
   * balloon이 차지하는 직사각형 영역의 시작점과 끝점을 계산하는 헬퍼함수
   * @param centerPos - balloon의 중심점 절대좌표
   * @param diameter - balloon의 지름
   * @returns 직사각형 영역의 시작점(좌상단)과 끝점(우하단)
   */
  private getBalloonBoundingRect(
    centerPos: Vector2d,
    diameter: number,
  ): { start: Vector2d; end: Vector2d } {
    const radius = diameter / 2
    return {
      start: {
        x: centerPos.x - radius,
        y: centerPos.y - radius,
      },
      end: {
        x: centerPos.x + radius,
        y: centerPos.y + radius,
      },
    }
  }

  /**
   * 다른 모든 balloon의 좌표평면상 범위를 캐싱하는 함수
   */
  private cacheOtherBalloonBoundingRects(): void {
    this.__otherBalloonBoundingRects = []

    // canvasEngine에서 모든 엔티티 가져오기
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allEntities = (this.canvasEngine as any).entities || []

    for (const entity of allEntities) {
      // 현재 엔티티, brush 타입 엔티티 제외
      if (entity.getId() === this.getId()) continue
      if (entity.isBrush && entity.isBrush()) continue

      // balloon이 있는 엔티티만 처리
      if (entity.balloon) {
        const balloonHead = entity.balloon.head.rect

        // 엔티티의 전역 좌표 계산
        let entityGlobalX: number = 0
        let entityGlobalY: number = 0

        if (entity.isLine && entity.isLine()) {
          // 선의 경우 중앙점 계산
          const points = entity.getPoints()
          entityGlobalX = (points[0] + points[2]) / 2
          entityGlobalY = (points[1] + points[3]) / 2
        } else if (
          entity.entityType === SHAPES.DIRECTION &&
          entity.__directionPoints &&
          entity.__directionPoints.length >= 4
        ) {
          // DIRECTION shape의 경우 balloon이 끝점에 고정되어 있으므로
          // balloon의 상대 좌표가 이미 절대 좌표임
          const balloonPos = entity.balloon.getPosition()
          const balloonGlobalCenter: Vector2d = {
            x: balloonPos.x,
            y: balloonPos.y,
          }
          const balloonDiameter = balloonHead.width()
          const boundingRect = this.getBalloonBoundingRect(balloonGlobalCenter, balloonDiameter)
          this.__otherBalloonBoundingRects.push({
            start: boundingRect.start,
            end: boundingRect.end,
            center: balloonGlobalCenter, // 실제 중심점 저장
            entity: entity,
            needPos: {
              type: 'top',
              x: balloonGlobalCenter.x,
              y: balloonGlobalCenter.y,
            },
            distance: 0,
          })
          continue // 일반적인 계산을 건너뛰고 다음 엔티티로
        } else {
          // 일반 도형의 중심 좌표
          entityGlobalX = entity.node.x()
          entityGlobalY = entity.node.y()
        }

        // balloon의 전역 좌표 계산
        const balloonPos = entity.balloon.getPosition()
        const balloonGlobalCenter: Vector2d = {
          x: entityGlobalX + balloonPos.x,
          y: entityGlobalY + balloonPos.y,
        }

        // balloon의 지름과 bounding rect 계산
        const balloonDiameter = balloonHead.width()
        const boundingRect = this.getBalloonBoundingRect(balloonGlobalCenter, balloonDiameter)
        this.__otherBalloonBoundingRects.push({
          start: boundingRect.start,
          end: boundingRect.end,
          center: balloonGlobalCenter, // 실제 중심점 저장
          entity: entity,
          needPos: {
            type: 'top',
            x: balloonGlobalCenter.x,
            y: balloonGlobalCenter.y,
          },
          distance: 0,
        })
      }
    }
  }

  calculateOtherBalloonPosition(_type: SHAPES, pos: Vector2d): Vector2d {
    // 다른 모든 balloon의 좌표평면상 범위 캐싱
    this.cacheOtherBalloonBoundingRects()

    //balloon size에 따라 변경된 y좌표를 대상으로 계산
    const targetY = pos.y - this.balloonSize / 2
    // 현재 생성하는 balloon의 절대 위치 계산
    const newBalloonPos = this.getBalloonAbsolutePosition({ x: pos.x, y: targetY })

    // 다른 balloon이 없으면 원래 위치 반환
    if (this.__otherBalloonBoundingRects.length === 0) {
      return {
        x: pos.x,
        y: targetY,
      }
    }

    // 현재 위치에서 실제 겹침이 있는지 체크
    if (!this.checkCollisionAtPosition(newBalloonPos)) {
      return {
        x: pos.x,
        y: targetY,
      }
    }

    // 재귀적 최적 위치 찾기
    const optimalPosition = this.findOptimalBalloonPosition(newBalloonPos, 0)

    // 절대좌표를 상대좌표로 변환
    const entityX = this.node.x()
    const entityY = this.node.y()

    return {
      x: optimalPosition.pos.x - entityX,
      y: optimalPosition.pos.y - entityY,
    }
  }

  /**
   * 재귀적으로 최적의 balloon 위치를 찾는 함수
   * @param newBalloonCenter - 새 balloon의 중심점 (절대좌표)
   * @param recursionDepth - 재귀 깊이 (무한 호출 방지용)
   * @returns 최적의 위치와 방향 정보
   */
  private findOptimalBalloonPosition(
    newBalloonCenter: Vector2d,
    recursionDepth: number,
  ): { pos: Vector2d; direction: string } {
    // 무한 호출 방지: 최대 10번까지만 재귀 호출
    if (recursionDepth > 10) {
      const closestBalloon = this.findClosestBalloon(newBalloonCenter)
      if (closestBalloon) {
        const forcedPos = this.calculatePositionInDirection(
          closestBalloon.center,
          'right',
          closestBalloon.size,
        )
        return { pos: forcedPos, direction: 'right' }
      }
      return { pos: newBalloonCenter, direction: 'none' }
    }

    // 현재 위치에서 겹치는 balloon들만 찾기
    const collidingBalloons = this.findCollidingBalloons(newBalloonCenter)

    if (collidingBalloons.length === 0) {
      return { pos: newBalloonCenter, direction: 'none' }
    }

    // 겹치는 balloon들 중 가장 가까운 것을 target으로 선정
    const targetBalloon = collidingBalloons[0] // 이미 거리순 정렬됨

    // 배치 우선순위: right, top 순서 (left, bottom 완전 제외)
    const directions: Array<'right' | 'top'> = ['right', 'top']

    // Target balloon의 2방향(right, top)을 순차적으로 시도
    for (const direction of directions) {
      const candidatePos = this.calculatePositionInDirection(
        targetBalloon.center,
        direction,
        targetBalloon.size,
      )
      if (!this.checkCollisionAtPosition(candidatePos)) {
        return { pos: candidatePos, direction }
      }
    }

    // Target balloon의 모든 방향(right, top)이 충돌하면 right 방향으로 이동 후 재귀 호출
    const rightPos = this.calculatePositionInDirection(
      targetBalloon.center,
      'right',
      targetBalloon.size,
    )

    // 새로운 위치에서 재귀 호출 (새로운 위치에서 다시 겹치는 balloon들을 찾게 됨)
    return this.findOptimalBalloonPosition(rightPos, recursionDepth + 1)
  }

  /**
   * 현재 위치에서 실제로 겹치는 balloon들만 찾아서 거리순으로 정렬하여 반환하는 함수
   * @param newBalloonCenter - 새 balloon의 중심점 (절대좌표)
   * @returns 겹치는 balloon들을 거리순으로 정렬한 배열
   */
  private findCollidingBalloons(
    newBalloonCenter: Vector2d,
  ): Array<{ center: Vector2d; distance: number; size: number }> {
    const collidingBalloons: Array<{ center: Vector2d; distance: number; size: number }> = []
    const COLLISION_TOLERANCE = 5 // 5px 여유를 두어 실질적인 겹침만 감지

    // 여유를 고려한 새 balloon의 bounding rect (5px씩 축소)
    const newBalloonRect = this.getBalloonBoundingRect(newBalloonCenter, this.balloonSize)
    const tolerantNewRect = {
      start: {
        x: newBalloonRect.start.x + COLLISION_TOLERANCE,
        y: newBalloonRect.start.y + COLLISION_TOLERANCE,
      },
      end: {
        x: newBalloonRect.end.x - COLLISION_TOLERANCE,
        y: newBalloonRect.end.y - COLLISION_TOLERANCE,
      },
    }

    for (const balloonRect of this.__otherBalloonBoundingRects) {
      // 허용 오차를 고려한 겹침 판단
      const isOverlapping = !(
        (
          tolerantNewRect.end.x < balloonRect.start.x || // new가 existing보다 왼쪽에 위치
          tolerantNewRect.start.x > balloonRect.end.x || // new가 existing보다 오른쪽에 위치
          tolerantNewRect.end.y < balloonRect.start.y || // new가 existing보다 위쪽에 위치
          tolerantNewRect.start.y > balloonRect.end.y
        ) // new가 existing보다 아래쪽에 위치
      )

      if (isOverlapping) {
        // 저장된 실제 중심점 사용 (정밀도 보장)
        const balloonCenter = balloonRect.center
        const balloonSize = balloonRect.end.x - balloonRect.start.x

        const distance = Math.sqrt(
          Math.pow(newBalloonCenter.x - balloonCenter.x, 2) +
            Math.pow(newBalloonCenter.y - balloonCenter.y, 2),
        )
        collidingBalloons.push({ center: balloonCenter, distance, size: balloonSize })
      }
    }

    // 거리순으로 정렬 (가까운 순서)
    return collidingBalloons.sort((a, b) => a.distance - b.distance)
  }

  /**
   * 모든 balloon을 거리순으로 정렬해서 반환하는 함수
   * @param newBalloonCenter - 새 balloon의 중심점
   * @returns 거리순으로 정렬된 balloon 정보 배열
   */
  private getAllBalloonsSortedByDistance(
    newBalloonCenter: Vector2d,
  ): Array<{ center: Vector2d; distance: number; size: number }> {
    const balloons: Array<{ center: Vector2d; distance: number; size: number }> = []

    for (const balloonRect of this.__otherBalloonBoundingRects) {
      // 저장된 실제 중심점 사용 (정밀도 보장)
      const balloonCenter = balloonRect.center

      // 기존 balloon의 크기 계산 (bounding rect에서 추출)
      const balloonSize = balloonRect.end.x - balloonRect.start.x

      const distance = Math.sqrt(
        Math.pow(newBalloonCenter.x - balloonCenter.x, 2) +
          Math.pow(newBalloonCenter.y - balloonCenter.y, 2),
      )

      balloons.push({ center: balloonCenter, distance, size: balloonSize })
    }

    // 거리순으로 정렬 (가까운 순서)
    return balloons.sort((a, b) => a.distance - b.distance)
  }

  /**
   * 가장 가까운 balloon을 찾는 함수
   * @param newBalloonCenter - 새 balloon의 중심점
   * @returns 가장 가까운 balloon 정보 또는 null
   */
  private findClosestBalloon(
    newBalloonCenter: Vector2d,
  ): { center: Vector2d; distance: number; size: number } | null {
    const allBalloons = this.getAllBalloonsSortedByDistance(newBalloonCenter)
    return allBalloons.length > 0 ? allBalloons[0] : null
  }

  /**
   * 특정 방향에서 새 balloon의 위치를 계산하는 함수
   * @param baseBalloonCenter - 기준 balloon의 중심점
   * @param direction - 배치 방향 (left, bottom 제외)
   * @param baseBalloonSize - 기준 balloon의 크기 (지름)
   * @returns 계산된 새 balloon의 중심점
   */
  private calculatePositionInDirection(
    baseBalloonCenter: Vector2d,
    direction: 'right' | 'top',
    baseBalloonSize: number = this.balloonSize, // 기본값은 새 balloon 크기
  ): Vector2d {
    // 올바른 거리 계산: 기존 balloon 반지름 + 새 balloon 반지름 (딱 붙게 배치)
    const baseBalloonRadius = baseBalloonSize / 2
    const newBalloonRadius = this.balloonSize / 2
    const safeDistance = baseBalloonRadius + newBalloonRadius // 두 반지름만 (근접 스냅과 동일하게 딱 붙게)

    switch (direction) {
      case 'right':
        return {
          x: baseBalloonCenter.x + safeDistance,
          y: baseBalloonCenter.y,
        }
      case 'top':
        return {
          x: baseBalloonCenter.x,
          y: baseBalloonCenter.y - safeDistance,
        }
    }
  }

  /**
   * 특정 위치에서 다른 balloon과 충돌하는지 체크하는 함수
   * @param candidatePos - 체크할 위치
   * @returns 충돌 여부 (true: 충돌함, false: 충돌하지 않음)
   */
  private checkCollisionAtPosition(candidatePos: Vector2d): boolean {
    const COLLISION_TOLERANCE = 5 // 5px 여유를 두어 실질적인 충돌만 감지
    const candidateRect = this.getBalloonBoundingRect(candidatePos, this.balloonSize)

    // 여유를 고려한 candidate balloon의 bounding rect (5px씩 축소)
    const tolerantCandidateRect = {
      start: {
        x: candidateRect.start.x + COLLISION_TOLERANCE,
        y: candidateRect.start.y + COLLISION_TOLERANCE,
      },
      end: {
        x: candidateRect.end.x - COLLISION_TOLERANCE,
        y: candidateRect.end.y - COLLISION_TOLERANCE,
      },
    }

    for (const balloonRect of this.__otherBalloonBoundingRects) {
      // 허용 오차를 고려한 충돌 판단
      const isOverlapping = !(
        (
          tolerantCandidateRect.end.x < balloonRect.start.x || // candidate가 balloon보다 왼쪽에 위치
          tolerantCandidateRect.start.x > balloonRect.end.x || // candidate가 balloon보다 오른쪽에 위치
          tolerantCandidateRect.end.y < balloonRect.start.y || // candidate가 balloon보다 위쪽에 위치
          tolerantCandidateRect.start.y > balloonRect.end.y
        ) // candidate가 balloon보다 아래쪽에 위치
      )

      if (isOverlapping) {
        return true // 충돌 발견
      }
    }

    return false // 충돌 없음
  }

  addBalloon(
    color: string,
    balloonFontColor: string,
    balloonFillColor: string,
    balloonText: string,
    pos: Vector2d,
    interactionMode: boolean = true,
    isExistingShape: boolean = false,
  ): void {
    if (this.isBackgroundEntity) return

    // Balloon 초기 위치 계산
    const initialPosition = this.calculateInitialBalloonPosition(pos, isExistingShape)

    // Balloon 설정
    const balloonConfig: BalloonConfig = {
      color,
      fillColor: balloonFillColor,
      fontColor: balloonFontColor,
      text: balloonText,
      size: this.balloonSize,
      strokeWidth: this.canvasEngine.connectedLineWidth,
      fontSize: this.canvasEngine.balloonFontSize,
      interactionMode,
      isSelected: this.isSelected,
      isTransparentLine: this.entityType === SHAPES.DIRECTION,
    }

    // Balloon 생성
    this.balloon = new Balloon(this.renderer, balloonConfig, initialPosition)

    // Balloon Group을 Entity에 추가
    this.addBalloonToEntity()

    // Balloon 드래그 diff 초기화
    this.balloonDragDiff = {
      x: initialPosition.x,
      y: initialPosition.y,
    }

    // Balloon 이벤트 등록
    this.setupBalloonEvents(interactionMode)

    // Shape Transform 이벤트 등록
    this.setupShapeTransformEvents()

    // 초기 Balloon 위치 업데이트
    this.updateBalloonPosition()
  }

  /**
   * Balloon 초기 위치 계산
   */
  private calculateInitialBalloonPosition(pos: Vector2d, isExistingShape: boolean): Vector2d {
    const startPath = pos ? pos : { x: 0, y: 0 }
    let balloonX = startPath.x
    let balloonY = startPath.y + BALLOON_LENGTH
    let positionAdjustedByCollision = false // 충돌 회피로 위치가 조정되었는지 추적

    if (this.entityType === SHAPES.DIRECTION) {
      // 방향도형의 경우 벌룬 중심점이 끝점에 위치 (절대좌표를 상대좌표로 변환)
      balloonX = startPath.x - this.node.x()
      balloonY = startPath.y - this.node.y()
    } else if (this.entityType === SHAPES.TRIPOD) {
      // TRIPOD의 경우 절대좌표를 상대좌표로 변환하고 주축선 기준으로 위치 계산
      const tripodBalloonPosition = this.calculateTripodBalloonPosition(startPath)
      balloonX = tripodBalloonPosition.x
      balloonY = tripodBalloonPosition.y
    } else if (!this.isLine()) {
      if (!isExistingShape) {
        balloonX = 0
        balloonY = BALLOON_LENGTH
      }
    } else {
      balloonY = startPath.y
    }

    if (this.isEllipse()) {
      balloonY = BALLOON_LENGTH + this.avaliableShape.height() / 2
      if (!isExistingShape) {
        const { x, y } = this.calculateOtherBalloonPosition(this.entityType, {
          x: balloonX,
          y: balloonY,
        })
        balloonX = x
        balloonY = y
        positionAdjustedByCollision = true // 충돌 회피로 위치 조정됨
      }
    }

    if (this.isRect()) {
      // RECT shape의 경우 shape 영역 위쪽 중간에 balloon 위치
      balloonX = this.getShapeWidth() / 2
      balloonY = BALLOON_LENGTH
      if (!isExistingShape) {
        const { x, y } = this.calculateOtherBalloonPosition(this.entityType, {
          x: balloonX,
          y: balloonY,
        })
        balloonX = x
        balloonY = y
        positionAdjustedByCollision = true // 충돌 회피로 위치 조정됨
      }
    }

    // 방향도형이 아닌 경우에만 벌룬 중심점 조정
    // 단, 충돌 회피로 이미 위치가 조정된 경우는 제외 (이미 절대좌표 기반으로 계산됨)
    if (this.entityType !== SHAPES.DIRECTION && !positionAdjustedByCollision) {
      balloonY = balloonY - this.balloonSize / 2 // balloonSize / 2 = balloon 반지름 (중심점 조정용)
    }

    return { x: balloonX, y: balloonY }
  }

  /**
   * Balloon을 Entity에 추가
   */
  private addBalloonToEntity(): void {
    if (!this.balloon) return

    if (this.entityType === SHAPES.TRIPOD && this.__tripod_transform_group) {
      // TRIPOD의 경우 balloon도 transformer 그룹에 포함
      this.__tripod_transform_group.add(this.balloon.group)
    } else if (this.__shape_wrapper) {
      this.__shape_wrapper.add(this.balloon.group)
      this.node.add(this.__shape_wrapper)
    } else {
      this.node.add(this.balloon.group)
    }
  }

  /**
   * Balloon 이벤트 설정
   */
  public setupBalloonEvents(interactionMode: boolean): void {
    if (!this.balloon) return

    const balloonHead = this.balloon.head.rect

    // 마우스 커서 변경
    this.balloon.onHead(CANVAS_ENTITY_EVENT.BALLOON_MOUSE_ENTER, () => {
      if (this.canvasEngine.isEntityCreating || !interactionMode) return
      this.canvasEngine.getStage().container().style.cursor = 'move'
    })

    this.balloon.onHead(CANVAS_ENTITY_EVENT.BALLOON_MOUSE_LEAVE, () => {
      this.canvasEngine.getStage().container().style.cursor = 'default'
    })

    // Draggable 설정
    this.balloon.onHead(
      `${CANVAS_ENTITY_EVENT.BALLOON_MOUSE_DOWN} ${CANVAS_ENTITY_EVENT.BALLOON_TOUCH_START}`,
      (e: IPointerEvent) => {
        if (e.evt instanceof MouseEvent) {
          if (!interactionMode || !this.isSelected) {
            balloonHead.draggable(false)
            return
          }
          if (
            this.canvasEngine.getHandleMode() === CANVAS_HANDLE_MODE.AUTO_ALIGN ||
            e.evt.button != 0
          ) {
            balloonHead.draggable(false)
            return
          }
        } else if (e.evt instanceof TouchEvent) {
          if (e.evt.touches.length > 1 || !this.isSelected) {
            balloonHead.draggable(false)
            return
          }
        }

        if (this.entityType === SHAPES.DIRECTION && !this.isSelected) {
          balloonHead.draggable(false)
          return
        }

        this.canvasEngine.getStage().container().style.cursor = 'move'
        balloonHead.draggable(true)
      },
    )

    // Drag 이벤트
    this.setupBalloonDragEvents()

    // Hover 이벤트
    this.balloon.onHead('mouseenter', () => this.mouseHover())
    this.balloon.onHead('mouseleave', () => this.mouseLeave())

    // Connected Line 이벤트 (방향도형이 아닌 경우에만)
    if (this.entityType !== SHAPES.DIRECTION) {
      this.balloon.onLine('mouseenter', () => this.mouseHover())
      this.balloon.onLine('mouseleave', () => this.mouseLeave())
    }
  }

  /**
   * Balloon Drag 이벤트 설정
   */
  private setupBalloonDragEvents(): void {
    if (!this.balloon) return

    this.balloon.onHead('dragstart', () => {
      this.snapManager.setDragStartPointerPos(this.canvasEngine.getPointerPosition())
      this.snapManager.beginDrag()

      if (this.entityType === SHAPES.DIRECTION) this.mouseLeave()

      if (!this.rippleManager.getRippleFollowMode()) {
        this.rippleManager.removeShapeAnimation()
      }
    })

    this.balloon.onHead('dragmove', () => {
      this.handleBalloonDragMove()
    })

    this.balloon.onHead('dragend', () => {
      this.snapManager.endDrag()

      if (this.entityType === SHAPES.DIRECTION && this.balloonDragDiff && this.balloon) {
        const balloonPos = this.balloon.getPosition()
        this.balloonDragDiff.x = balloonPos.x
        this.balloonDragDiff.y = balloonPos.y
      }

      if (!this.rippleManager.getRippleFollowMode()) {
        this.rippleManager.addShapeAnimation()
      } else {
        this.rippleManager.updateRipplePosition()
      }

      this.canvasEngine.onEntityTransform(this)
    })
  }

  /**
   * Balloon Drag Move 처리
   */
  private handleBalloonDragMove(): void {
    if (!this.balloonDragDiff || !this.balloon) {
      throw new Error('balloonDragDiff가 없습니다.')
    }

    this.isBalloonMoved = true

    // TRIPOD 특수 처리
    if (this.isTripod()) {
      this.handleTripodBalloonDrag()
      return
    }

    // 스냅 평가: Konva가 포인터 기준으로 옮겨둔 현재 위치가 곧 스냅 없는 가상 위치
    const virtualPosition = this.getCurrentBalloonGlobalPosition()
    const snappedPosition = this.snapManager.evaluateSnap(virtualPosition)

    if (snappedPosition) {
      this.moveBalloonToGlobalPosition(snappedPosition)
    } else {
      this.updateBalloonPositionWithCollisionCheck()
    }

    // balloonDragDiff 업데이트
    if (this.entityType !== SHAPES.DIRECTION && !this.snapManager.getIsSnapped()) {
      const balloonPos = this.balloon.getPosition()
      this.balloonDragDiff.x = balloonPos.x
      this.balloonDragDiff.y = balloonPos.y
    }

    // 파동 애니메이션 업데이트
    if (this.rippleManager.getRippleFollowMode()) {
      this.rippleManager.updateRipplePosition()
    }

    this.node.getLayer()?.batchDraw()
  }

  /**
   * TRIPOD Balloon Drag 처리
   */
  private handleTripodBalloonDrag(): void {
    const tripodShapePoints = this.getTripodShapePoints()
    if (!tripodShapePoints || !this.balloon) return

    const pointerPosition = this.canvasEngine.getPointerPosition()
    const finalPosition = this.__calculateTripodBalloonPosition(pointerPosition, tripodShapePoints)
    this.moveBalloonToGlobalPosition(finalPosition)

    // TRIPOD connectedLine 강제 업데이트
    const newPoints = this.getTripodConnectorPoints()
    this.balloon.updateConnectedLinePoints(newPoints)

    const layer = this.balloon.connectedLine.line.getLayer()
    if (layer) {
      layer.batchDraw()
    }
  }

  /**
   * Shape Transform 이벤트 설정
   */
  private setupShapeTransformEvents(): void {
    if (!this.isEllipse() && !this.isRect()) return
    if (!this.balloon) return

    this.avaliableShape.on('transformstart', () => {
      this.isBalloonMoved = true
    })

    this.avaliableShape.on('transform', () => {
      this.updateBalloonPosition()

      if (this.__cloudBorder) {
        this.updateCloudBorderRotation()
      }

      this.updateFillPatternOnTransform()

      if (!this.balloonDragDiff) {
        throw new Error('balloonDragDiff가 없습니다.')
      }

      if (!this.snapManager.getIsSnapped() && this.balloon) {
        const balloonPos = this.balloon.getPosition()
        this.balloonDragDiff.x = balloonPos.x
        this.balloonDragDiff.y = balloonPos.y
      }
    })

    this.avaliableShape.on('dragmove', () => {
      if (!this.snapManager.getIsSnapped()) {
        this.updateBalloonPosition()
      }
    })
  }

  /*
    Line에서 Points를 옮길 때, 끝 점에 Balloon이 따라가게 하기.
  */
  syncBalloonToLine(newPoints: number[]): void {
    if (!this.balloon) return

    const diff = this.connectToLinePosition(newPoints)
    const currentPos = this.balloon.getPosition()

    diff.x = diff.x - this.balloon.previousPosition.x
    diff.y = diff.y - this.balloon.previousPosition.y

    this.balloon.setPosition({
      x: currentPos.x + diff.x,
      y: currentPos.y + diff.y,
    })

    const points = this.getConnectorPoints()
    this.balloon.updateConnectedLinePoints([
      points[0] + diff.x,
      points[1] + diff.y,
      points[2] + diff.x,
      points[3] + diff.y,
    ])

    this.balloon.head.updateTextPosition()
    this.balloon.previousPosition = this.connectToLinePosition(newPoints)
  }

  moveBallon(pos: Vector2d): void {
    if (this.isBackgroundEntity) return
    if (!this.balloon) {
      throw new Error('풍선 객체가 없습니다.')
    }

    if (this.isLine()) {
      // 선 인 경우, group이 항상 0,0이므로 선의 첫 좌표를 기준으로 풍선을 이동
      this.balloon.setPosition({
        x: this.getPoints()[0] + pos.x,
        y: this.getPoints()[1] + pos.y,
      })
      this.balloon.previousPosition = this.connectToLinePosition()
    } else {
      this.balloon.setPosition(pos)
    }

    // 풍선좌표는 기본적으로 x는 0, y는 -40 또는 -45 이므로 이 좌표가 아닌 경우에만 풍선을 이동한 것으로 취급.
    if (!(pos.x == 0 && (pos.y == -40 || pos.y == -45))) {
      if (!this.balloonDragDiff) {
        throw new Error('balloonDragDiff가 없습니다.')
      }
      this.isBalloonMoved = true

      const balloonPos = this.balloon.getPosition()
      this.balloonDragDiff.x = balloonPos.x
      this.balloonDragDiff.y = balloonPos.y
    }
    this.updateBalloonPosition()
  }

  updateStatus(): void {
    super.updateStatus()
    if (!this.balloon) return
  }

  setBalloonText(text: string): void {
    if (!this.balloon) return
    const isTextChanged = this.balloon.head.text.text() !== text
    this.balloon.setText(text)
    // 캐시된 엔티티는 화면에 캐시 비트맵(옛 번호)이 그려지므로, 번호가 실제로 바뀐
    // 엔티티만 캐시를 무효화해 다음 batchDraw가 라이브 드로우로 새 번호를 표시하게 함.
    // 즉시 재캐시는 하지 않음 — updateIndex 루프에서 엔티티 수만큼 동기 cache()가 실행되어
    // 기존 배치 캐시 경로의 프레임 예산·과대 엔티티 가드를 우회하기 때문.
    // 재캐시는 기존 배치 경로(refreshCache/refreshAsyncCache/deferredCacheEntities)가 수행함.
    if (isTextChanged && this.renderer.hints.isStatic(this.node)) {
      this.renderer.hints.unmarkStatic(this.node)
    }
  }

  setBalloonAdminText(text: string): void {
    if (!this.balloon) return
    this.__admin_balloon_text = text
    this.setBalloonText(this.__admin_balloon_text)
  }

  protected updateBalloonPosition(): void {
    if (!this.avaliableShape) return
    if (!this.balloon) return

    // Connected Line 업데이트 (방향도형이 아닌 경우)
    if (this.entityType !== SHAPES.DIRECTION) {
      const connectorPoints = this.getConnectorPoints()
      this.balloon.updateConnectedLinePoints(connectorPoints)
    } else {
      this.balloon.setLineVisible(false)
    }

    // POINT 화살표 모드: 풍선 방향에 맞춰 화살표 머리 회전
    if (this.isPoint() && this.__individualDotTypeSetting === 'arrow') {
      this.updatePointArrowHeadGeometry()
    }

    // Text 위치 업데이트
    this.balloon.head.updateTextPosition()

    // Tag 위치 업데이트
    this.balloon.updateTagPosition()

    // Balloon을 파동보다 위에 배치
    this.rippleManager.ensureBalloonAboveRipples()

    this.node.getLayer()?.batchDraw()
  }

  // 충돌 체크가 포함된 balloon 위치 업데이트
  protected updateBalloonPositionWithCollisionCheck(): void {
    if (!this.avaliableShape) return
    if (!this.balloon) return

    // 현재 balloon의 전역 위치 계산
    const currentBalloonGlobalPosition = this.getCurrentBalloonGlobalPosition()

    // 충돌 체크 및 위치 조정 (전역 좌표 기준)
    const collisionFreeGlobalPosition = this.snapManager.getCollisionFreePosition(
      currentBalloonGlobalPosition,
    )

    // 위치가 조정되었다면 balloon 위치 업데이트
    if (
      collisionFreeGlobalPosition.x !== currentBalloonGlobalPosition.x ||
      collisionFreeGlobalPosition.y !== currentBalloonGlobalPosition.y
    ) {
      // 전역 좌표를 상대 좌표로 변환하여 설정
      const entityPos = this.isLine()
        ? {
            x: (this.getPoints()[0] + this.getPoints()[2]) / 2,
            y: (this.getPoints()[1] + this.getPoints()[3]) / 2,
          }
        : { x: this.node.x(), y: this.node.y() }

      const relativePosition = {
        x: collisionFreeGlobalPosition.x - entityPos.x,
        y: collisionFreeGlobalPosition.y - entityPos.y,
      }

      this.balloon.setPosition(relativePosition)
    }

    // Connected Line 업데이트
    if (this.entityType !== SHAPES.DIRECTION) {
      this.balloon.updateConnectedLinePoints(this.getConnectorPoints())
    } else {
      this.balloon.setLineVisible(false)
    }

    // POINT 화살표 모드: 풍선 방향에 맞춰 화살표 머리 회전
    if (this.isPoint() && this.__individualDotTypeSetting === 'arrow') {
      this.updatePointArrowHeadGeometry()
    }

    // Text 위치 업데이트
    this.balloon.head.updateTextPosition()

    // Balloon을 파동보다 위에 배치
    this.rippleManager.ensureBalloonAboveRipples()

    this.node.getLayer()?.batchDraw()
  }

  protected getCenter(shape: IShape | IGroup): Vector2d {
    if (isLineNode(shape)) {
      return {
        x: (shape.points()[0] + shape.points()[2]) / 2,
        y: (shape.points()[1] + shape.points()[3]) / 2,
      }
    } else if (isRectNode(shape)) {
      const clientRect = shape.getClientRect({ relativeTo: shape.getParent()! })

      // 경계 사각형의 중심 좌표를 계산합니다.
      const centerX = clientRect.x + clientRect.width / 2
      const centerY = clientRect.y + clientRect.height / 2

      return {
        x: centerX,
        y: centerY,
      }
    } else if (isEllipseNode(shape)) {
      return {
        x: shape.x(),
        y: shape.y(),
      }
    }
    return {
      x: shape.x(),
      y: shape.y(),
    }
  }

  protected getConnectorPoints(): number[] {
    if (!this.balloon) return [0, 0, 0, 0]

    // TRIPOD의 경우 주축선에 연결
    if (this.isTripod()) {
      return this.getTripodConnectorPoints()
    }

    const balloonCenter: Vector2d = this.balloon.getPosition()

    // POINT의 경우 간단하게 처리
    if (this.isPoint()) {
      // BallonShape의 경계 지점 계산
      const ballonRadius = this.balloonSize / 2

      // point의 실제 시각적 중심
      const pointCenter = {
        x: 0,
        y: 0,
      }

      // balloon에서 point 중심으로의 각도
      const angleFromEdgeToCenter = Math.atan2(
        balloonCenter.y - pointCenter.y,
        balloonCenter.x - pointCenter.x,
      )

      // balloon 경계점 (balloon에서 point 방향으로)
      const toEdgeX = balloonCenter.x - ballonRadius * Math.cos(angleFromEdgeToCenter)
      const toEdgeY = balloonCenter.y - ballonRadius * Math.sin(angleFromEdgeToCenter)

      return [toEdgeX, toEdgeY, pointCenter.x, pointCenter.y]
    }

    const closetPoint = this.drawLineToNearestEdge(balloonCenter)

    // BallonShape의 경계 지점 계산
    const ballonRadius = this.balloonSize / 2 // balloonSize / 2 = balloon 반지름 (연결점 계산용)

    const angleFromEdgeToCenter = Math.atan2(
      balloonCenter.y - closetPoint.y,
      balloonCenter.x - closetPoint.x,
    )

    const toEdgeX = balloonCenter.x - ballonRadius * Math.cos(angleFromEdgeToCenter)
    const toEdgeY = balloonCenter.y - ballonRadius * Math.sin(angleFromEdgeToCenter)

    return [toEdgeX, toEdgeY, closetPoint.x, closetPoint.y]
  }

  // 지정된 점까지 가장 가까운 선을 그리는 함수
  protected drawLineToNearestEdge(balloonPoint: Vector2d): Vector2d {
    const shapeType = this.entityType
    const shapeRotation = this.getRotate()
    const shapeCenter = this.getCenter(this.avaliableShape)

    if (this.isPoint()) {
      // POINT의 실제 시각적 중심
      return {
        x: 0,
        y: 0,
      }
    } else if (shapeType === SHAPES.RECT) {
      const rectWidth = this.getShapeWidth()
      const rectHeight = this.getShapeHeight()

      return getClosestPointOnRectangle(
        shapeCenter,
        rectWidth,
        rectHeight,
        balloonPoint,
        shapeRotation,
      )
    } else if (this.isLine()) {
      // 선의 경우 중점 반환 (상대좌표)
      return {
        x: (this.getPoints()[0] + this.getPoints()[2]) / 2,
        y: (this.getPoints()[1] + this.getPoints()[3]) / 2,
      }
    } else if (
      this.entityType === SHAPES.DIRECTION &&
      this.__directionPoints &&
      this.__directionPoints.length >= 4
    ) {
      // 방향도형의 경우 points의 마지막 요소 2개가 연결점
      return {
        x: this.__directionPoints[this.__directionPoints.length - 2],
        y: this.__directionPoints[this.__directionPoints.length - 1],
      }
    }
    return {
      x: 0,
      y: 0,
    }
  }

  /**
   * [렌더링 전용] 렌더링 하기 위해 풍선의 우선순위를 변경.
   * @returns
   */
  ungroupBalloon(): void {
    if (!this.balloon) return

    // 객체들을 그룹에서 제거
    this.balloon.head.rect.remove()
    this.balloon.head.text.remove()

    // 현재 레이어에 직접 추가
    const layer = this.renderer.layers.system

    const balloonPos = this.balloon.getPosition()
    this.balloon.head.rect.x(balloonPos.x + this.node.x())
    this.balloon.head.rect.y(balloonPos.y + this.node.y())

    this.balloon.head.text.x(balloonPos.x + this.node.x())
    this.balloon.head.text.y(balloonPos.y + this.node.y())

    layer.add(this.balloon.head.rect)
    layer.add(this.balloon.head.text)

    // 풍선과 글자만 최상위로 위치.
    this.balloon.moveToTop()
  }

  updateConnectedLinePoints(points: number[]): void {
    if (!this.balloon) return
    this.balloon.updateConnectedLinePoints(points)
    if (this.isPoint() && this.__individualDotTypeSetting === 'arrow') {
      this.updatePointArrowHeadGeometry()
    }
  }

  protected __mouseHoverBalloonStrokeWidth: number = 0
  protected __mouseHoverConnectedLineStrokeWidth: number = 0
  protected __originalBalloonStrokeWidth: number = 0
  protected __originalConnectedLineStrokeWidth: number = 0

  /**
   * 마우스 호버시 강조효과를 나타냄.
   */
  mouseHover(_?: MouseEvent | TouchEvent): void {
    // 벌룬 드래그 중이면 hover 효과 적용하지 않음
    if (this.balloon?.isDragging()) return
    super.mouseHover()
  }

  /**
   * 마우스 떠날때 강조효과를 해제함.
   */
  mouseLeave(_?: MouseEvent | TouchEvent): void {
    super.mouseLeave()
  }

  /**
   * DIRECTION shape의 현재 bend point 개수를 반환합니다.
   * @returns bend point 개수 (최대 2개)
   */
  protected getDirectionShapeState(): 'vertical' | 'horizontal' | 'diagonal' | 'normal' {
    if (
      this.entityType !== SHAPES.DIRECTION ||
      !this.__directionPoints ||
      this.__directionPoints.length < 4
    ) {
      // 방향도형이 아니면 판단하지 않음
      return 'normal'
    }

    //끝점
    const lastX = this.__directionPoints[this.__directionPoints.length - 2]
    const lastY = this.__directionPoints[this.__directionPoints.length - 1]

    //끝에서 두번째 점
    const prevX = this.__directionPoints[this.__directionPoints.length - 4]
    const prevY = this.__directionPoints[this.__directionPoints.length - 3]

    if (prevY === lastY) {
      return 'vertical' // X좌표가 거의 같으면 수직 상태
    }
    if (prevX === lastX) {
      return 'horizontal' // Y좌표가 거의 같으면 수평 상태
    }
    if (lastX !== prevX && lastY !== prevY) {
      return 'diagonal'
    }

    return 'normal' // 둘 다 아니면 대각선 상태
  }
  /**
   * 현재 드래그가 대각선 이동인지 판단합니다.
   * @returns true면 대각선 이동, false면 수직/수평 이동
   */
  protected isDiagonalMovement(): boolean {
    const dragStartPos = this.snapManager.getDragStartPointerPos()
    if (this.entityType !== SHAPES.DIRECTION || !dragStartPos) {
      return false
    }

    const currentPointerPos = this.canvasEngine.getPointerPosition()
    const dx = Math.abs(currentPointerPos.x - dragStartPos.x)
    const dy = Math.abs(currentPointerPos.y - dragStartPos.y)
    const tolerance = 50 // 수직/수평으로 간주할 허용 오차 범위

    // X와 Y 이동량이 모두 tolerance보다 크면 대각선 이동으로 판단
    return dx > tolerance && dy > tolerance
  }

  /**
   * balloon 드래그 상태를 설정합니다.
   * @param isDragging - 드래그 중인지 여부
   */
  public setBalloonDragMode(isDragging: boolean): void {
    this.isBalloonDragMode = isDragging
  }

  /**
   * 파동 애니메이션 모드 설정 (외부에서 접근 가능)
   */
  public setRippleFollowMode(followMode: boolean): void {
    this.rippleManager.setRippleFollowMode(followMode)
  }

  /**
   * 파동 애니메이션 모드 조회 (외부에서 접근 가능)
   */
  public getRippleFollowMode(): boolean {
    return this.rippleManager.getRippleFollowMode()
  }

  // 풍선을 전역 좌표 기준으로 이동시키는 함수
  // 주의) direction 도형은 setPoints를 사용해주세요 (마지막 2개가 벌룬의 중심좌표임)
  moveBalloonToGlobalPosition(globalPosition: Vector2d): void {
    if (!this.balloon) return

    try {
      this.isBalloonMoved = true

      // 풍선 위치 업데이트
      if (this.isLine()) {
        const points = this.getPoints()
        // 선의 경우 선의 시작점을 기준으로 상대적인 풍선 위치 계산
        this.moveBallon({
          x: globalPosition.x - points[0],
          y: globalPosition.y - points[1],
        })
      } else {
        this.moveBallon({
          x: globalPosition.x - this.node.x(),
          y: globalPosition.y - this.node.y(),
        })
      }
    } catch (error) {
      console.error('벌룬 위치 이동 중 에러:', error)
      // 에러 발생 시 스냅 상태 해제
      this.snapManager.resetSnapState()
    }
  }

  /**
   * TRIPOD 전용 connector points 계산
   * balloon을 주축선에 연결
   */
  private getTripodConnectorPoints(): number[] {
    if (!this.balloon) return [0, 0, 0, 0]

    const tripodShapePoints = this.getTripodShapePoints()
    if (!tripodShapePoints) return [0, 0, 0, 0]

    // entity의 상대좌표를 절대좌표로 변환
    const entityX = this.node.x()
    const entityY = this.node.y()

    const balloonPos = this.balloon.getPosition()
    const balloonAbsolute: Vector2d = {
      x: entityX + balloonPos.x,
      y: entityY + balloonPos.y,
    }

    // balloon 위치를 주축선에 투영하여 연결점 찾기
    const connectionPoint = this.__projectPointOntoTripodMainAxis(
      balloonAbsolute,
      tripodShapePoints,
    )

    // 결과를 상대좌표로 변환
    const currentBalloonPos = this.balloon.getPosition()
    const shapeX = connectionPoint.x - entityX
    const shapeY = connectionPoint.y - entityY

    // balloon 경계에서 시작하도록 오프셋 (투명 채움 시 연결선이 벌룬 내부를 통과해 보이지 않도록)
    return this.offsetConnectorStartToBalloonEdge(currentBalloonPos, { x: shapeX, y: shapeY })
  }

  /**
   * 벌룬 중심에서 연결점으로 향하는 선의 시작점을 벌룬 경계로 오프셋한 points를 반환합니다.
   * 벌룬 채움이 투명일 때 연결선이 벌룬 내부를 통과해 보이는 것을 방지합니다.
   * @param balloonCenter - 벌룬 중심 (상대좌표)
   * @param connectionPoint - 연결점 (상대좌표)
   * @returns [시작x, 시작y, 끝x, 끝y] (연결점이 벌룬 안이면 길이 0 선분)
   */
  protected offsetConnectorStartToBalloonEdge(
    balloonCenter: Vector2d,
    connectionPoint: Vector2d,
  ): number[] {
    const radius = this.balloonSize / 2
    const dx = connectionPoint.x - balloonCenter.x
    const dy = connectionPoint.y - balloonCenter.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // 연결점이 벌룬 내부면 선을 그리지 않음
    if (distance <= radius) {
      return [connectionPoint.x, connectionPoint.y, connectionPoint.x, connectionPoint.y]
    }

    return [
      balloonCenter.x + (dx / distance) * radius,
      balloonCenter.y + (dy / distance) * radius,
      connectionPoint.x,
      connectionPoint.y,
    ]
  }

  /**
   * TRIPOD balloon 위치를 계산 (상대좌표)
   * 주축선 중앙에서 시작점들의 반대편에 위치
   */
  private calculateTripodBalloonPosition(_absolutePosition: Vector2d): Vector2d {
    const tripodShapePoints = this.getTripodShapePoints()
    if (!tripodShapePoints) {
      return { x: 0, y: -50 }
    }

    // 절대좌표를 entity의 상대좌표로 변환
    const entityX = this.node.x()
    const entityY = this.node.y()

    // 주축선 중앙점 계산 (상대좌표)
    const mainAxisStart = tripodShapePoints.mainAxisPoint1
    const mainAxisEnd = tripodShapePoints.mainAxisPoint2
    const mainAxisCenterX = (mainAxisStart[0] + mainAxisEnd[0]) / 2 - entityX
    const mainAxisCenterY = (mainAxisStart[1] + mainAxisEnd[1]) / 2 - entityY

    // 시작점들의 평균 위치 계산 (상대좌표)
    const avgStartX =
      tripodShapePoints.dots.reduce((sum, dot) => sum + dot[0], 0) / tripodShapePoints.dots.length -
      entityX
    const avgStartY =
      tripodShapePoints.dots.reduce((sum, dot) => sum + dot[1], 0) / tripodShapePoints.dots.length -
      entityY

    // 주축선이 수직인지 수평인지 판단
    const isVerticalMainAxis =
      Math.abs(mainAxisEnd[1] - mainAxisStart[1]) > Math.abs(mainAxisEnd[0] - mainAxisStart[0])

    let balloonX: number, balloonY: number

    if (isVerticalMainAxis) {
      // 수직 주축선: 시작점들과 주축선의 x 위치 비교
      const balloonOnRightSide = avgStartX < mainAxisCenterX
      balloonX = mainAxisCenterX + (balloonOnRightSide ? 50 : -50)
      balloonY = mainAxisCenterY
    } else {
      // 수평 주축선: 시작점들과 주축선의 y 위치 비교
      const balloonBelowAxis = avgStartY > mainAxisCenterY
      balloonX = mainAxisCenterX
      balloonY = mainAxisCenterY + (balloonBelowAxis ? -50 : 50)
    }

    return { x: balloonX, y: balloonY }
  }

  /**
   * TRIPOD balloon 위치 계산 (주축선 범위 제한 + 스냅 기능)
   */
  private __calculateTripodBalloonPosition(
    pointerPosition: { x: number; y: number },
    tripodShapePoints: TripodShapePoints,
  ): { x: number; y: number } {
    const mainAxisStart = {
      x: tripodShapePoints.mainAxisPoint1[0],
      y: tripodShapePoints.mainAxisPoint1[1],
    }
    const mainAxisEnd = {
      x: tripodShapePoints.mainAxisPoint2[0],
      y: tripodShapePoints.mainAxisPoint2[1],
    }

    // 주축선 벡터와 방향
    const mainAxisVector = {
      x: mainAxisEnd.x - mainAxisStart.x,
      y: mainAxisEnd.y - mainAxisStart.y,
    }
    const mainAxisLength = Math.sqrt(
      mainAxisVector.x * mainAxisVector.x + mainAxisVector.y * mainAxisVector.y,
    )
    const mainAxisDirection = {
      x: mainAxisVector.x / mainAxisLength,
      y: mainAxisVector.y / mainAxisLength,
    }

    // 마우스 위치를 주축선에 투영
    const mouseToStart = {
      x: pointerPosition.x - mainAxisStart.x,
      y: pointerPosition.y - mainAxisStart.y,
    }
    const projectedDistance =
      mouseToStart.x * mainAxisDirection.x + mouseToStart.y * mainAxisDirection.y

    // 주축선 범위 내로 제한 (양끝점 이상으로 나가지 못함)
    const clampedDistance = Math.max(0, Math.min(mainAxisLength, projectedDistance))

    // 제한된 투영점 계산
    const projectedPoint = {
      x: mainAxisStart.x + mainAxisDirection.x * clampedDistance,
      y: mainAxisStart.y + mainAxisDirection.y * clampedDistance,
    }

    // 주축선에 수직인 방향으로의 거리 계산
    const perpendicular = {
      x: -mainAxisDirection.y,
      y: mainAxisDirection.x,
    }
    const perpendicularDistance =
      (pointerPosition.x - projectedPoint.x) * perpendicular.x +
      (pointerPosition.y - projectedPoint.y) * perpendicular.y

    // 기본 balloon 위치
    let finalBalloonPosition = {
      x: projectedPoint.x + perpendicular.x * perpendicularDistance,
      y: projectedPoint.y + perpendicular.y * perpendicularDistance,
    }

    // 스냅 기능 적용
    finalBalloonPosition = this.__applyTripodBalloonSnap(
      finalBalloonPosition,
      projectedPoint,
      tripodShapePoints,
    )

    return finalBalloonPosition
  }

  /**
   * TRIPOD balloon 스냅 기능 적용
   */
  private __applyTripodBalloonSnap(
    balloonPosition: { x: number; y: number },
    projectedPoint: { x: number; y: number },
    tripodShapePoints: TripodShapePoints,
  ): { x: number; y: number } {
    const mainAxisStart = {
      x: tripodShapePoints.mainAxisPoint1[0],
      y: tripodShapePoints.mainAxisPoint1[1],
    }
    const mainAxisEnd = {
      x: tripodShapePoints.mainAxisPoint2[0],
      y: tripodShapePoints.mainAxisPoint2[1],
    }

    // 주축선 중앙점
    const mainAxisCenter = {
      x: (mainAxisStart.x + mainAxisEnd.x) / 2,
      y: (mainAxisStart.y + mainAxisEnd.y) / 2,
    }

    // 1. 주축선 중앙에서 스냅 (임계값 5)
    const distanceToCenter = Math.sqrt(
      Math.pow(projectedPoint.x - mainAxisCenter.x, 2) +
        Math.pow(projectedPoint.y - mainAxisCenter.y, 2),
    )
    if (distanceToCenter <= 5) {
      const mainAxisDirection = {
        x: mainAxisEnd.x - mainAxisStart.x,
        y: mainAxisEnd.y - mainAxisStart.y,
      }
      const mainAxisLength = Math.sqrt(
        mainAxisDirection.x * mainAxisDirection.x + mainAxisDirection.y * mainAxisDirection.y,
      )
      const normalizedDirection = {
        x: mainAxisDirection.x / mainAxisLength,
        y: mainAxisDirection.y / mainAxisLength,
      }
      const perpendicular = {
        x: -normalizedDirection.y,
        y: normalizedDirection.x,
      }

      const perpendicularDistance =
        (balloonPosition.x - mainAxisCenter.x) * perpendicular.x +
        (balloonPosition.y - mainAxisCenter.y) * perpendicular.y

      return {
        x: mainAxisCenter.x + perpendicular.x * perpendicularDistance,
        y: mainAxisCenter.y + perpendicular.y * perpendicularDistance,
      }
    }

    // 2. startPoint들과 일직선에서 스냅 (임계값 10)
    for (const dot of tripodShapePoints.dots) {
      const startPoint = { x: dot[0], y: dot[1] }
      const startPointProjected = this.__projectPointOntoTripodMainAxis(
        startPoint,
        tripodShapePoints,
      )

      const distanceToStartPointLine = Math.sqrt(
        Math.pow(projectedPoint.x - startPointProjected.x, 2) +
          Math.pow(projectedPoint.y - startPointProjected.y, 2),
      )

      if (distanceToStartPointLine <= 10) {
        const mainAxisDirection = {
          x: mainAxisEnd.x - mainAxisStart.x,
          y: mainAxisEnd.y - mainAxisStart.y,
        }
        const mainAxisLength = Math.sqrt(
          mainAxisDirection.x * mainAxisDirection.x + mainAxisDirection.y * mainAxisDirection.y,
        )
        const normalizedDirection = {
          x: mainAxisDirection.x / mainAxisLength,
          y: mainAxisDirection.y / mainAxisLength,
        }
        const perpendicular = {
          x: -normalizedDirection.y,
          y: normalizedDirection.x,
        }

        const perpendicularDistance =
          (balloonPosition.x - startPointProjected.x) * perpendicular.x +
          (balloonPosition.y - startPointProjected.y) * perpendicular.y

        return {
          x: startPointProjected.x + perpendicular.x * perpendicularDistance,
          y: startPointProjected.y + perpendicular.y * perpendicularDistance,
        }
      }
    }

    // 스냅되지 않으면 원래 위치 반환
    return balloonPosition
  }

  // RECT 도형 transform 시 balloon 위치를 절대좌표 기준으로 고정
  override transformShape(): void {
    if (this.entityType === SHAPES.RECT) {
      if (!this.__shape) return
      const shape = this.__shape

      // balloon이 있는 경우 현재 절대좌표 저장 (회전/크기 변경 시 고정하기 위함)
      let balloonGlobalPos: Vector2d | null = null
      if (this.balloon) {
        balloonGlobalPos = this.getCurrentBalloonGlobalPosition()
      }

      // 위치 조정
      this.node.x(this.node.x() + shape.x())
      this.node.y(this.node.y() + shape.y())

      // 크기 조정 — 0/극소 스케일 프레임은 무시 (멀티터치로 Transformer 박스가 0으로
      // 클램프된 프레임을 흡수하면 width=0이 저장되어 재로드 시 기본크기로 리셋됨)
      const newWidth = shape.width() * shape.scaleX()
      const newHeight = shape.height() * shape.scaleY()
      if (newWidth > 0.5) shape.width(newWidth)
      if (newHeight > 0.5) shape.height(newHeight)

      // 구름 테두리가 있다면 크기 변경 시에만 재생성
      if (this.__cloudBorder) {
        this.updateCloudBorderIfSizeChanged()
      }

      // fill 패턴이 적용되어 있다면 크기 변경에 맞게 재생성
      this.updateFillPatternOnResize()

      // 초기화
      shape.scaleX(1)
      shape.scaleY(1)
      shape.x(0)
      shape.y(0)

      // balloon 절대좌표 복원 (회전/크기 변경 후 balloon은 그대로 유지)
      if (balloonGlobalPos && this.balloon) {
        // 새로운 entity 위치 기준으로 상대좌표 재계산하여 balloon 배치
        const newRelativePos = {
          x: balloonGlobalPos.x - this.node.x(),
          y: balloonGlobalPos.y - this.node.y(),
        }
        this.balloon.setPosition(newRelativePos)

        // balloonDragDiff도 업데이트
        if (this.balloonDragDiff) {
          this.balloonDragDiff.x = newRelativePos.x
          this.balloonDragDiff.y = newRelativePos.y
        }

        // connectedLine만 업데이트
        this.updateBalloonPosition()
      }
    } else {
      // RECT가 아닌 경우 부모 메서드 호출
      super.transformShape()
    }
  }
}
